import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GitBranch, DollarSign, TrendingUp, Users, Sparkles, ArrowRight, Trash2, Share2 } from 'lucide-react';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { useOs2, PageHead, Card, money, Fade, LoadingState, STAGE_GRADIENTS, RANGES } from './os2ui';
import { AnimatedNumber, OsCard } from '../../os3/theme';
import SalesPage from './SalesPage';
import MarketingOsPage from './MarketingOsPage';
import CustomersPage from './CustomersPage';
import { Customer360Drawer } from './Customer360Drawer';

/* Revenue Pipeline v3 — flagship OS module: funnel + intelligence sub-tabs. */

const LABELS = {
  visitors: ['Visitors', 'Visitantes'],
  scans: ['Index AI Scans', 'Scans Index AI'],
  reports: ['Reports', 'Reportes'],
  leads: ['Leads', 'Leads'],
  qualified: ['Qualified', 'Calificados'],
  calls: ['Calls', 'Llamadas'],
  proposals: ['Proposals', 'Propuestas'],
  customers: ['Customers', 'Clientes'],
};

const TABS = [
  { key: 'pipeline', icon: GitBranch, en: 'Pipeline', es: 'Pipeline' },
  { key: 'sales', icon: DollarSign, en: 'Sales Intelligence', es: 'Inteligencia de Ventas' },
  { key: 'marketing', icon: TrendingUp, en: 'Marketing Intelligence', es: 'Inteligencia de Marketing' },
  { key: 'customers', icon: Users, en: 'Customer Intelligence', es: 'Inteligencia de Clientes' },
];

function CleanupButton({ isEs, reload }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await authFetch('/os2/cleanup-test?dry_run=true', { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      if (!j.total) {
        window.alert(isEs ? 'No quedan datos de prueba — todo es real ✓' : 'No test data left — everything is real ✓');
        return;
      }
      const scanList = j.scans.slice(0, 8).map((s) => `· ${s.domain} (${s.score ?? '—'})`).join('\n');
      const leadList = j.leads.slice(0, 8).map((l) => `· ${l.name || l.email}`).join('\n');
      const msg = isEs
        ? `Se eliminarán ${j.scans.length} scan(s) y ${j.leads.length} lead(s) de PRUEBA:\n\n${scanList}${j.scans.length > 8 ? '\n…' : ''}\n${leadList}${j.leads.length > 8 ? '\n…' : ''}\n\nLos datos reales NO se tocan. Quedará notificado en el Event Bus. ¿Continuar?`
        : `${j.scans.length} TEST scan(s) and ${j.leads.length} TEST lead(s) will be removed:\n\n${scanList}${j.scans.length > 8 ? '\n…' : ''}\n${leadList}${j.leads.length > 8 ? '\n…' : ''}\n\nReal data is NOT touched. The Event Bus will be notified. Continue?`;
      if (!window.confirm(msg)) return;
      const r2 = await authFetch('/os2/cleanup-test?dry_run=false', { method: 'POST' });
      const j2 = await r2.json();
      if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
      window.alert(isEs
        ? `Limpieza completada ✓ — ${j2.scans_removed} scans y ${j2.leads_removed} leads de prueba eliminados.`
        : `Cleanup done ✓ — ${j2.scans_removed} test scans and ${j2.leads_removed} test leads removed.`);
      reload();
    } catch (ex) {
      window.alert(String(ex.message));
    } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid="pipeline-cleanup-test"
      title={isEs ? 'Elimina scans y leads de prueba (example.com, kitxens.com, emails test/demo). Los datos reales no se tocan.' : 'Removes test scans and leads (example.com, kitxens.com, test/demo emails). Real data is untouched.'}
      className="mr-auto h-8 px-3.5 rounded-full border text-[12px] font-semibold transition-colors disabled:opacity-50"
      style={{ borderColor: 'var(--os-border)', color: 'var(--os-ink-soft)', background: 'var(--os-surface)' }}>
      🧹 {busy ? '…' : (isEs ? 'Limpiar datos de prueba' : 'Clean test data')}
    </button>
  );
}

const AI_SCORE_COLOR = (s) => (s >= 70 ? '#059669' : s >= 40 ? '#F77F11' : '#9ca3af');

function InviteScanBtn({ email, invited, isEs }) {
  const [state, setState] = useState(invited ? 'sent' : 'idle');
  const send = async (e) => {
    e.stopPropagation();
    setState('busy');
    try {
      const r = await authFetch(`/os2/lead/${encodeURIComponent(email)}/invite-scan`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error();
      setState('sent');
    } catch {
      setState('err');
      setTimeout(() => setState('idle'), 2500);
    }
  };
  if (state === 'sent') {
    return <span className="text-[9.5px] font-bold text-emerald-600 uppercase" data-testid={`invite-sent-${email}`}>{isEs ? 'Invitación AI Score enviada ✓' : 'AI Score invite sent ✓'}</span>;
  }
  return (
    <button onClick={send} disabled={state === 'busy'} data-testid={`invite-scan-${email}`}
      title={isEs ? 'Rachel le envía un email invitándole a medir su AI Score (Index AI) gratis' : 'Rachel emails them an invite to run their free AI Score (Index AI)'}
      className="text-[9.5px] font-bold uppercase text-[#F77F11] hover:underline disabled:opacity-50">
      {state === 'err' ? (isEs ? 'Error — reintenta' : 'Error — retry') : state === 'busy' ? '…' : (isEs ? 'Invitar a su AI Score →' : 'Invite to AI Score →')}
    </button>
  );
}

const CHANNEL_LABEL = { whatsapp: 'WhatsApp', x: 'X', copy: 'Link', email: 'Rachel email' };
const PAGE_SIZE = 10;

function ShareRow({ s, isEs, onOpen, onDelete }) {
  const [remState, setRemState] = useState(s.reminded_at ? 'sent' : 'idle');
  const [delState, setDelState] = useState('idle');
  const STATUS = {
    entered: { es: 'Entró a la demo', en: 'Entered the demo', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
    requested: { es: 'Pidió acceso', en: 'Requested access', cls: 'bg-orange-50 border-orange-200 text-[#d8651a]' },
    invited: { es: 'Invitado', en: 'Invited', cls: 'bg-gray-100 border-gray-200 text-gray-500' },
  };
  const remind = async () => {
    setRemState('busy');
    try {
      const r = await authFetch(`/os2/demo-shares/${s.id}/remind`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error();
      setRemState('sent');
    } catch { setRemState('err'); setTimeout(() => setRemState('idle'), 2500); }
  };
  const del = async () => {
    if (delState === 'idle') { setDelState('confirm'); setTimeout(() => setDelState((st) => (st === 'confirm' ? 'idle' : st)), 3500); return; }
    setDelState('busy');
    try {
      const r = await authFetch(`/os2/demo-shares/${s.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error();
      onDelete(s.id);
    } catch { setDelState('idle'); }
  };
  return (
    <div className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]" data-testid={`share-row-${s.id}`}>
      <span className="rounded bg-gray-100 text-gray-600 px-1.5 py-px text-[9.5px] font-bold uppercase shrink-0">{CHANNEL_LABEL[s.channel] || s.channel}</span>
      {s.locale && <span className="rounded bg-blue-50 border border-blue-100 text-blue-500 px-1 py-px text-[9px] font-bold uppercase shrink-0">{s.locale.slice(0, 2)}</span>}
      {s.partner_ref && (
        <span className="rounded bg-violet-50 border border-violet-200 text-violet-600 px-1.5 py-px text-[9.5px] font-bold uppercase shrink-0" data-testid={`share-partner-${s.id}`}>
          Partner · {s.partner_ref}
        </span>
      )}
      {s.sharer_email ? (
        <button onClick={() => onOpen(s.sharer_email)} className="font-semibold text-gray-800 hover:text-[#F77F11] hover:underline truncate" data-testid={`share-sharer-${s.id}`}>
          {s.sharer_name || s.sharer_email}
        </button>
      ) : (
        <span className="text-gray-400 italic">{isEs ? 'anónimo' : 'anonymous'}</span>
      )}
      {s.friend_email && <span className="text-gray-500 truncate">→ {s.friend_email}</span>}
      {s.friend_status && STATUS[s.friend_status] && (
        <span className={`rounded border px-1.5 py-px text-[9.5px] font-bold uppercase ${STATUS[s.friend_status].cls}`}>
          {isEs ? STATUS[s.friend_status].es : STATUS[s.friend_status].en}
        </span>
      )}
      <span className="ml-auto flex items-center gap-2 shrink-0">
        {(s.friend_email || s.sharer_email) && (
          <button onClick={remind} disabled={remState === 'busy' || remState === 'sent'} data-testid={`share-remind-${s.id}`}
            title={isEs ? 'Rachel les recuerda su visita y les ofrece una auditoría gratuita (free audit) en su idioma' : 'Rachel reminds them of their visit and offers a free audit in their language'}
            className={`text-[9.5px] font-bold uppercase ${remState === 'sent' ? 'text-emerald-600' : 'text-[#F77F11] hover:underline'} disabled:cursor-default`}>
            {remState === 'sent' ? (isEs ? 'Recordado ✓' : 'Reminded ✓') : remState === 'busy' ? '…' : remState === 'err' ? 'Error' : (isEs ? 'Recordar + Free audit' : 'Remind + Free audit')}
          </button>
        )}
        <button onClick={del} disabled={delState === 'busy'} data-testid={`share-delete-${s.id}`}
          title={isEs ? 'Eliminar registro (se notifica al AI Team)' : 'Delete record (AI Team gets notified)'}
          className={`text-[9.5px] font-bold uppercase ${delState === 'confirm' ? 'text-red-600' : 'text-gray-300 hover:text-red-500'}`}>
          {delState === 'confirm' ? (isEs ? '¿Seguro?' : 'Sure?') : delState === 'busy' ? '…' : <Trash2 className="w-3.5 h-3.5 inline" />}
        </button>
        <span className="text-[10.5px] text-gray-300">{s.at ? new Date(s.at).toLocaleDateString(isEs ? 'es' : 'en', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
      </span>
    </div>
  );
}

function SharesPanel({ isEs, onOpen }) {
  const [d, setD] = useState(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    authFetch(`/os2/demo-shares?limit=${PAGE_SIZE}&offset=${offset}`).then((r) => r.json()).then(setD)
      .catch(() => setD({ items: [], total: 0, by_channel: {}, friends_entered: 0 }));
  }, [offset]);
  if (!d) return null;
  const totalPages = Math.max(1, Math.ceil((d.total || 0) / PAGE_SIZE));
  const page = Math.floor(offset / PAGE_SIZE);
  const removeLocal = (id) => setD((cur) => ({ ...cur, total: cur.total - 1, items: cur.items.filter((i) => i.id !== id) }));
  const PgBtn = ({ label, target, disabled, testid }) => (
    <button onClick={() => setOffset(target)} disabled={disabled} data-testid={testid}
      className="h-6 min-w-[26px] px-1.5 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 hover:border-[#F77F11] hover:text-[#F77F11] transition-colors disabled:opacity-30 disabled:pointer-events-none text-[11px]">
      {label}
    </button>
  );
  return (
    <Fade delay={0.1}>
      <Card className="p-5 mt-4" data-testid="pipeline-shares-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-[14px] font-semibold tracking-tight flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Shares de la demo' : 'Demo shares'}
          </h3>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide" data-testid="shares-panel-stats">
            {d.total} {isEs ? 'shares' : 'shares'}
            {Object.entries(d.by_channel || {}).map(([c, n]) => ` · ${CHANNEL_LABEL[c] || c} ${n}`).join('')}
            {' · '}{d.friends_entered} {isEs ? 'amigos dentro' : 'friends in'}
          </p>
        </div>
        {!d.items.length ? (
          <p className="text-[12.5px] text-gray-400">{isEs ? 'Nadie ha compartido la demo todavía — cuando alguien pulse "Compartir demo", aparecerá aquí con su canal y su invitado.' : 'No one has shared the demo yet — when someone hits "Share demo", it will show here with channel and invitee.'}</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {d.items.map((s) => <ShareRow key={s.id} s={s} isEs={isEs} onOpen={onOpen} onDelete={removeLocal} />)}
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-3 flex items-center justify-end gap-1.5" data-testid="shares-pagination">
            <PgBtn label="«" target={0} disabled={page === 0} testid="shares-page-first" />
            <PgBtn label="‹" target={Math.max(0, offset - PAGE_SIZE)} disabled={page === 0} testid="shares-page-prev" />
            <span data-testid="shares-page-indicator" className="text-[10.5px] font-bold text-gray-400 px-1">{page + 1} / {totalPages}</span>
            <PgBtn label="›" target={offset + PAGE_SIZE} disabled={page >= totalPages - 1} testid="shares-page-next" />
            <PgBtn label="»" target={(totalPages - 1) * PAGE_SIZE} disabled={page >= totalPages - 1} testid="shares-page-last" />
          </div>
        )}
      </Card>
    </Fade>
  );
}

const PREV_LABEL = {
  1: ['yesterday', 'ayer'],
  yd: ['day before', 'anteayer'],
  7: ['prior 7 days', '7 días previos'],
  30: ['prior 30 days', '30 días previos'],
  90: ['prior 90 days', '90 días previos'],
  365: ['prior 12 months', '12 meses previos'],
};

function PrevBadge({ count, prev, isEs, range }) {
  if (prev == null) return null;
  const lbl = (PREV_LABEL[range] || ['prev. period', 'periodo anterior'])[isEs ? 1 : 0];
  if (prev === 0 && count === 0) {
    return <span className="text-[10px] font-semibold text-white/55 tabular-nums">= 0 vs {lbl}</span>;
  }
  if (prev === 0) {
    return <span className="text-[10px] font-bold tabular-nums" style={{ color: '#6EE7B7' }}>▲ +{count} vs {lbl}</span>;
  }
  const pct = Math.round(((count - prev) / prev) * 100);
  const up = pct >= 0;
  return (
    <span className="text-[10px] font-bold tabular-nums" style={{ color: up ? '#6EE7B7' : '#FCA5A5' }}>
      {up ? '▲' : '▼'} {Math.abs(pct)}% vs {lbl} ({prev})
    </span>
  );
}

function ConvBadge({ conv }) {
  const tone = conv >= 40 ? '#34D399' : conv >= 15 ? '#FBBF24' : '#F87171';
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums"
      style={{ background: 'rgba(0,0,0,0.28)', color: conv > 100 ? '#34D399' : tone }}>
      {conv > 100 ? '▲ 100%+' : `↓ ${conv}%`}
    </span>
  );
}

function Funnel({ d, isEs, sel, setSel, goTab, range, reload }) {
  const navigate = useNavigate();
  const [busyDel, setBusyDel] = useState(null);
  const [mPage, setMPage] = useState(0);
  const [sortAsc, setSortAsc] = useState(true);
  const [c360, setC360] = useState('');
  const max = Math.max(1, ...d.stages.map((s) => s.count));
  const selStage = d.stages.find((s) => s.key === sel);
  const PER = 10;
  const sortedMembers = selStage?.members
    ? [...selStage.members].sort((a, b) => ((a.at || '') < (b.at || '') ? -1 : 1) * (sortAsc ? 1 : -1))
    : [];
  const totalPages = Math.max(1, Math.ceil(sortedMembers.length / PER));
  const page = Math.min(mPage, totalPages - 1);
  const pageMembers = sortedMembers.slice(page * PER, page * PER + PER);
  const fmtDate = (iso) => {
    if (!iso) return '';
    const dt = new Date(iso);
    return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(isEs ? 'es' : 'en', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  const removeMember = async (m, e) => {
    e.stopPropagation();
    const label = m.name || m.email;
    if (!window.confirm(isEs
      ? `¿Eliminar "${label}" definitivamente? Se borra el registro (y sus leads asociados si es un scan). Quedará notificado en el Event Bus.`
      : `Permanently remove "${label}"? The record (and its associated leads if it's a scan) will be deleted. The Event Bus will be notified.`)) return;
    const key = m.scan_id || m.email;
    setBusyDel(key);
    try {
      const r = await authFetch(m.scan_id ? `/os2/scans/${m.scan_id}` : `/os2/lead/${encodeURIComponent(m.email)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      reload();
    } catch (ex) {
      window.alert(String(ex.message));
    } finally { setBusyDel(null); }
  };

  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5" data-testid="pipeline-funnel">
        <div className="xl:col-span-2 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {d.stages.map((s, i) => {
            const conv = i > 0 && d.stages[i - 1].count > 0
              ? Math.round((s.count / d.stages[i - 1].count) * 100) : null;
            const share = Math.max(0.22, Math.sqrt(s.count / max));
            const active = sel === s.key;
            return (
              <button key={s.key} onClick={() => { if (s.members) { setSel(active ? null : s.key); setMPage(0); } }} data-testid={`pipeline-stage-${s.key}`}
                className={`relative rounded-2xl text-left p-4 overflow-hidden os3-lift os3-rise ${active ? 'ring-2 ring-[#F77F11] ring-offset-2' : ''}`}
                style={{ background: STAGE_GRADIENTS[i % STAGE_GRADIENTS.length], animationDelay: `${i * 60}ms`,
                         boxShadow: active ? '0 0 26px 0 var(--os-glow)' : '0 10px 26px rgba(0,0,0,0.18)' }}>
                <span className="absolute -right-6 -top-6 w-24 h-24 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} aria-hidden="true" />
                <p className="text-[10px] uppercase tracking-[0.14em] text-white/75 font-bold truncate">{LABELS[s.key][isEs ? 1 : 0]}</p>
                <p className="text-[30px] font-black tabular-nums text-white leading-tight tracking-tight">
                  <AnimatedNumber value={s.count} />
                </p>
                <div className="mt-1.5 flex items-center justify-between">
                  {conv != null ? <ConvBadge conv={conv} /> : <span className="text-[10.5px] text-white/60 font-semibold">{isEs ? 'inicio' : 'top'}</span>}
                  {s.members && <ArrowRight className="w-3.5 h-3.5 text-white/70" aria-hidden="true" />}
                </div>
                {s.prev != null && (
                  <div className="mt-1" data-testid={`pipeline-prev-${s.key}`}>
                    <PrevBadge count={s.count} prev={s.prev} isEs={isEs} range={range} />
                  </div>
                )}
                <div className="mt-2.5 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.25)' }} aria-hidden="true">
                  <div className="h-full rounded-full bg-white/85 transition-[width] duration-700 ease-out" style={{ width: `${share * 100}%` }} />
                </div>
              </button>
            );
          })}
        </div>
        <OsCard className="p-5 relative overflow-hidden" glow data-testid="pipeline-recommendations">
          <span className="absolute inset-x-0 top-0 h-[2px]" style={{ background: 'linear-gradient(90deg,transparent,#F77F11,transparent)' }} aria-hidden="true" />
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold mb-3" style={{ color: 'var(--os-accent)' }}>
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> {isEs ? 'Qué hacer ahora' : 'What to do next'}
          </p>
          {(() => {
            const RECO = {
              scans: [isEs ? 'Pocos scans: promociona el Free Audit en redes con link UTM.' : 'Few scans: promote the Free Audit on social with a UTM link.', '/admin/ai-os/content', isEs ? 'Content & Distribution' : 'Content & Distribution'],
              reports: [isEs ? 'Scans sin reporte: revisa el listado de scans y quién no desbloqueó.' : 'Scans without reports: review the scans list and who did not unlock.', '/admin/ai-os/growth#scans', 'Growth Center · Scans'],
              leads: [isEs ? 'Reportes sin lead: refuerza el CTA post-scan (Marketing).' : 'Reports without leads: strengthen the post-scan CTA (Marketing).', '/admin/ai-os/pipeline?tab=marketing', 'Marketing Intelligence'],
              qualified: [isEs ? 'Leads sin calificar: revisa Sales Intelligence y da seguimiento.' : 'Unqualified leads: review Sales Intelligence and follow up.', '/admin/ai-os/pipeline?tab=sales', 'Sales Intelligence'],
              calls: [isEs ? 'Calificados sin llamada: agenda demos esta semana.' : 'Qualified without a call: book demos this week.', '/admin/ai-os/pipeline?tab=sales', 'Sales Intelligence'],
              proposals: [isEs ? 'Llamadas sin propuesta: envía pricing el mismo día.' : 'Calls without proposals: send pricing same-day.', '/admin/ai-os/pipeline?tab=sales', 'Sales Intelligence'],
              customers: [isEs ? 'Propuestas abiertas: invita al Founding Program para cerrar.' : 'Open proposals: invite to the Founding Program to close.', '/admin/ai-os/restaurants', 'Restaurants'],
            };
            const drops = d.stages.map((s, i) => ({
              key: s.key,
              conv: i > 0 && d.stages[i - 1].count > 0 ? s.count / d.stages[i - 1].count : null,
            })).filter((x) => x.conv != null && RECO[x.key]).sort((a, b) => a.conv - b.conv).slice(0, 3);
            return drops.map((x, i) => {
              const [text, to, dest] = RECO[x.key];
              const m = to.match(/pipeline\?tab=(\w+)/);
              return (
                <button key={x.key} data-testid={`pipeline-reco-${x.key}`}
                  onClick={() => { if (m) goTab(m[1]); else navigate(to); }}
                  className="block w-full text-left rounded-xl border px-3.5 py-2.5 mb-2 os3-lift os3-rise cursor-pointer"
                  style={{ borderColor: 'var(--os-border)', background: 'var(--os-surface-2)', animationDelay: `${200 + i * 90}ms` }}>
                  <p className="text-[11px] font-bold tabular-nums" style={{ color: '#F87171' }}>
                    {LABELS[x.key][isEs ? 1 : 0]} · ↓ {Math.round(x.conv * 100)}%
                  </p>
                  <p className="text-[12.5px] leading-snug" style={{ color: 'var(--os-ink-soft)' }}>{text}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-bold" style={{ color: 'var(--os-accent)' }}>
                    {isEs ? 'Abrir' : 'Open'} {dest} <ArrowRight className="w-3 h-3" />
                  </p>
                </button>
              );
            });
          })()}
          <p className="mt-2 text-[10px]" style={{ color: 'var(--os-ink-faint)' }}>
            {isEs ? 'Conversión desde la etapa anterior: verde ≥40%, ámbar ≥15%, rojo <15%. Barra = volumen relativo.' : 'Conversion from previous stage: green ≥40%, amber ≥15%, red <15%. Bar = relative volume.'}
          </p>
        </OsCard>
      </div>

      {selStage?.members && (
        <Fade delay={0.05} key={sel}>
          <Card className="mt-5 p-6" data-testid="pipeline-members">
            <h2 className="text-[15px] font-semibold tracking-tight mb-3">
              {isEs ? 'Quién está en' : "Who's in"} “{LABELS[sel][isEs ? 1 : 0]}” <span className="text-gray-400 font-normal">({sortedMembers.length})</span>
            </h2>
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="pipeline-members-controls">
              <button onClick={() => setSortAsc((v) => !v)} data-testid="pipeline-members-sort"
                className="h-7 px-3 rounded-full border border-gray-200 bg-white text-[11px] font-semibold text-gray-500 hover:border-gray-400 transition-colors">
                {isEs ? 'Fecha' : 'Date'} {sortAsc ? '↑' : '↓'} <span className="text-gray-300">·</span> {sortAsc ? (isEs ? 'antiguos primero' : 'oldest first') : (isEs ? 'recientes primero' : 'newest first')}
              </button>
              <span className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-gray-400 tabular-nums">
                <button onClick={() => setMPage(Math.max(0, page - 1))} disabled={page === 0} data-testid="pipeline-members-prev"
                  className="h-7 px-3 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35 hover:border-gray-400 transition-colors">‹ {isEs ? 'Atrás' : 'Back'}</button>
                <span className="px-1 font-semibold">{sortedMembers.length ? page * PER + 1 : 0}–{Math.min((page + 1) * PER, sortedMembers.length)} {isEs ? 'de' : 'of'} {sortedMembers.length}</span>
                <button onClick={() => setMPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} data-testid="pipeline-members-next"
                  className="h-7 px-3 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35 hover:border-gray-400 transition-colors">{isEs ? 'Adelante' : 'Next'} ›</button>
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {pageMembers.map((m, mi) => (
                <div key={m.scan_id || m.email || `${m.name}-${mi}`}
                  onClick={() => { if (m.email) setC360(m.email); }}
                  className={`group flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2 hover:border-[#F77F11]/40 transition-colors ${m.email ? 'cursor-pointer' : ''}`}
                  title={m.email ? (isEs ? 'Abrir ficha Customer 360' : 'Open Customer 360 card') : undefined}
                  data-testid={`pipeline-member-${m.email || m.name}`}>
                  <span className="w-8 h-8 shrink-0 rounded-full bg-[#FFF7ED] border border-orange-200 text-[#F77F11] text-[12px] font-bold flex items-center justify-center uppercase">
                    {(m.name || '?').slice(0, 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-gray-900 truncate">
                      {m.name}
                      {m.at && <span className="ml-1.5 text-[10px] font-semibold text-gray-400 tabular-nums">· {fmtDate(m.at)}</span>}
                    </p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {m.score != null && <>Index AI {m.score} · </>}
                      {m.anonymous ? (isEs ? 'Sin email — no desbloqueó' : 'No email — did not unlock') : (m.affiliate ? `via ${m.affiliate}` : m.email)}
                    </p>
                    {(m.tags?.length || m.stage) ? (
                      <p className="mt-1 flex flex-wrap gap-1" data-testid={`pipeline-member-tags-${m.email || m.name}`}>
                        {m.stage && <span className="rounded bg-orange-50 text-[#d8651a] px-1.5 py-px text-[9px] font-bold uppercase">{m.stage.replace('_', ' ')}</span>}
                        {(m.tags || []).map((t) => (
                          <span key={t} className="rounded bg-gray-100 text-gray-500 px-1.5 py-px text-[9px] font-semibold uppercase">{t.replace('_', ' ')}</span>
                        ))}
                      </p>
                    ) : null}
                    {m.email && m.score == null && (
                      <p className="mt-1"><InviteScanBtn email={m.email} invited={!!m.scan_invited} isEs={isEs} /></p>
                    )}
                  </div>
                  {m.ai_score != null && (
                    <span title={`AI Score ${m.ai_score}/100`} data-testid={`pipeline-ai-score-${m.email || m.name}`}
                      className="shrink-0 w-8 h-8 rounded-full border-2 text-[11px] font-black flex items-center justify-center tabular-nums bg-white"
                      style={{ borderColor: AI_SCORE_COLOR(m.ai_score), color: AI_SCORE_COLOR(m.ai_score) }}>
                      {m.ai_score}
                    </span>
                  )}
                  {(m.scan_id || m.email) && (
                    <button onClick={(e) => removeMember(m, e)} disabled={busyDel === (m.scan_id || m.email)}
                      title={isEs ? 'Eliminar registro (pruebas / no interesa)' : 'Remove record (tests / not interested)'}
                      data-testid={`pipeline-member-delete-${m.email || m.scan_id}`}
                      className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-40">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {!sortedMembers.length && <p className="text-sm text-gray-400">{isEs ? 'Nadie en esta etapa todavía' : 'No one in this stage yet'}</p>}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-[12px] font-bold">
              <button onClick={() => goTab('sales')} className="text-[#F77F11] hover:underline" data-testid="pipeline-members-open-sales">
                {isEs ? 'Ver todos en Sales Intelligence →' : 'View all in Sales Intelligence →'}
              </button>
              {sel === 'scans' && (
                <a href="/admin/ai-os/growth#scans" className="text-[#F77F11] hover:underline" data-testid="pipeline-members-open-scans">
                  {isEs ? 'Listado completo de scans en Growth Center →' : 'Full scans list in Growth Center →'}
                </a>
              )}
              {sel === 'customers' && (
                <button onClick={() => goTab('customers')} className="text-[#F77F11] hover:underline" data-testid="pipeline-members-open-customers">
                  {isEs ? 'Ver Customer Intelligence →' : 'View Customer Intelligence →'}
                </button>
              )}
            </div>
          </Card>
        </Fade>
      )}
      <SharesPanel isEs={isEs} onOpen={setC360} />
      {c360 && <Customer360Drawer email={c360} isEs={isEs} onClose={() => setC360('')} />}
    </>
  );
}

export default function PipelinePage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => (TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'pipeline'));
  const [sel, setSel] = useState(null);
  const [range, setRange] = useState(() => localStorage.getItem('kx_os2_days') || '');
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/pipeline');
  const setPeriod = (v) => {
    setRange(v);
    localStorage.setItem('kx_os2_days', v);
    window.dispatchEvent(new Event('kx-os2-range'));
  };

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  return (
    <div data-testid="os2-pipeline">
      <PageHead
        title="Revenue Pipeline"
        question={isEs ? 'El flujo completo del negocio: de visitante a MRR — en una sola pantalla.' : 'The full business flow: from visitor to MRR — in one screen.'}
        fresh={{ at: fetchedAt, onReload: reload, loading, isEs }}
        module="AI OS"
        right={
          <OsCard className="px-5 py-3" data-testid="pipeline-mrr">
            <p className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: 'var(--os-accent)' }}>MRR</p>
            <p className="text-xl font-black tabular-nums leading-tight" style={{ color: 'var(--os-ink)' }}>
              <AnimatedNumber value={d.mrr} format={(v) => money(v)} />
            </p>
          </OsCard>
        }
      />

      {/* Period selector — right-aligned under the MRR card; the whole pipeline follows this range */}
      <div className="-mt-4 mb-4 flex flex-wrap items-center gap-1.5 justify-end" data-testid="pipeline-period">
        <CleanupButton isEs={isEs} reload={reload} />
        <span className="text-[10.5px] uppercase tracking-[0.15em] font-bold mr-1" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'Periodo' : 'Period'}</span>
        {RANGES.map(([v, en, es]) => (
          <button key={v} onClick={() => setPeriod(v)} data-testid={`pipeline-period-${v || 'lifetime'}`}
            className={`h-8 px-3.5 rounded-full border text-[12px] font-semibold transition-colors ${range === v ? 'text-white' : ''}`}
            style={range === v
              ? { background: 'var(--os-accent)', borderColor: 'var(--os-accent)' }
              : { borderColor: 'var(--os-border)', color: 'var(--os-ink-soft)', background: 'var(--os-surface)' }}>
            {isEs ? es : en}
          </button>
        ))}
      </div>

      {/* Intelligence sub-tabs */}
      <div className="mb-6 flex md:inline-flex items-center gap-1 rounded-full border p-1 max-w-full overflow-x-auto" role="tablist"
        style={{ borderColor: 'var(--os-border)', background: 'var(--os-surface)' }} data-testid="pipeline-tabs">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            data-testid={`revenue-pipeline-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${
              tab === t.key ? 'text-white' : ''}`}
            style={tab === t.key
              ? { background: 'var(--os-accent)', boxShadow: '0 0 14px 0 var(--os-glow)' }
              : { color: 'var(--os-ink-soft)' }}>
            <t.icon className="w-3.5 h-3.5" aria-hidden="true" />
            {isEs ? t.es : t.en}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && <Funnel d={d} isEs={isEs} sel={sel} setSel={setSel} goTab={setTab} range={range} reload={reload} />}
      {tab === 'sales' && <div className="os3-rise"><SalesPage embedded /></div>}
      {tab === 'marketing' && <div className="os3-rise"><MarketingOsPage embedded /></div>}
      {tab === 'customers' && <div className="os3-rise"><CustomersPage embedded /></div>}
    </div>
  );
}
